import React, { useEffect, useState, useContext } from "react";
import setHtml from "ui/js/utils/setHtml";
import SpeedVisual from "~/blocks/SpeedVisual";
import { injectLegalAndTooltips } from "ui/js/utils/legal";
import { splitPrice, stripCurrency } from "ui/js/utils/products";
import PRODUCTS from "~/constants/products";
import Tooltip from "ui/components/Tooltip";
import useWindowSize from "~/hooks/useWindowSize";
import { v4 as uuidv4 } from "uuid";
import DynamicButton from "ui/components/Buttons/DynamicButton";
import styles from "./styles/index.module.css";
import { useKeenSlider } from "keen-slider/react";
import { PricingContext } from "ui/providers/pricing";
import { Pricing } from "ui/types/common";
import { Product } from "./types";

import "keen-slider/keen-slider.min.css";

type CardData = {
  title: string | JSX.Element;
  speed: string;
  mostPopular?: boolean;
  features: any;
  tooltip: string;
  class: string;
  cardBanner: any;
  speedVisuals: { handle: string; graph: number };
  expansionHeading: any;
};

type FiberPlans = {
  fiber500: CardData;
  fiberGig: CardData;
  fiber2Gig: CardData;
  fiber5Gig: CardData;
  fiber7Gig: CardData;
};

const Cards = ({ ...props }) => {
  const {
    position,
    cardsCtaType,
    customStyles,
    productsArray,
    isDoubleDouble,
  } = props;

  const [slidesPerView, setSlidesPerView] = useState(1);
  const { width } = useWindowSize();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderLoaded, setSliderLoaded] = useState(false);
  const [sliderRef, instanceRef] = useKeenSlider({
    mode: "free-snap",
    initial: 2,
    loop: false,
    slides: {
      origin: "center",
      perView: 1,
      spacing: 20,
    },
    breakpoints: {
      "(min-width: 375px)": {
        slides: {
          origin: "center",
          perView: 1.1,
          spacing: 10,
        },
      },
      "(min-width: 450px)": {
        slides: {
          origin: "center",
          perView: 1.3,
          spacing: 10,
        },
      },
      "(min-width: 570px)": {
        slides: {
          origin: "center",
          perView: 1.75,
          spacing: 20,
        },
      },
      "(min-width: 650px)": {
        slides: {
          origin: "center",
          perView: 2,
          spacing: 20,
        },
      },
      "(min-width: 850px)": {
        slides: {
          origin: "center",
          perView: 2.5,
          spacing: 20,
        },
      },
      "(min-width: 1000px)": {
        slides: {
          origin: "center",
          perView: 3.5,
          spacing: 20,
        },
      },
      "(min-width: 1300px)": {
        slides: {
          origin: "center",
          perView: 4.5,
          spacing: 20,
        },
      },
      "(min-width: 1650px)": {
        disabled: true,
      },
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created(slider) {
      setSliderLoaded(true);
      setCurrentSlide(slider.track.details.rel);
    },
  });
  const { pricing } = useContext(PricingContext) as unknown as { pricing: Pricing };

  const products: (keyof FiberPlans)[] = [
    "fiber500",
    "fiberGig",
    "fiber2Gig",
    "fiber5Gig",
    "fiber7Gig",
  ];
  const productsLength = products.length;

  useEffect(() => {
    const containerWidth = Math.min(width, 1440);
    const result = Math.max(containerWidth / 326, 1);
    setSlidesPerView(result);
    instanceRef?.current?.update();
  }, [slidesPerView, width, products]);

  useEffect(() => {
    const id = uuidv4();
    setInstanceId(id);
    initMatchHeight();
  }, []);

  const initMatchHeight = () => {
    import("@tannerhodges/match-height").then((MatchHeight) => {
      MatchHeight.add("div[data-match-height]", "product-features");
      MatchHeight.add("p[data-match-height]", "product-heading");
    });
  };

  const [instanceId, setInstanceId] = useState();

  const toggleDetails = (product: string) => {
    const element = document.getElementById(`js-value-props-${product}`);
    const button = document.getElementById(`js-expansion-btn-${product}`);
    if (element?.classList.contains("is-open")) {
      element.classList.remove("is-open");
      if (button)
        button.innerHTML = `More details <span class="icon icon-chevron-down ${styles.component__iconChevron}"/>`;
    } else {
      element?.classList.add("is-open");
      if (button)
        button.innerHTML = `See less <span class="icon icon-chevron-up ${styles.component__iconChevron}"/>`;
    }
  };

  const transformedProducts = productsArray.reduce((acc: { [key: string]: Product }, product: Product) => {
    acc[product.id] = product;
    return acc;
  }, {});

  const supplementalData: FiberPlans = {
    fiber500: {
      title: <span { ...setHtml( transformedProducts.fiber500?.heading ) } />,
      speed: "500/500 Mbps",
      features: transformedProducts.fiber500?.features ?? [],
      tooltip:
        "per month w/ bank Auto Pay & Paperless Bill, for 24 months. One-time charges may apply. Wi-Fi, actual & average speeds vary.",
      class: "_500",
      cardBanner: transformedProducts.fiber500?.banner ?? {},
      speedVisuals: {
        handle: "12%",
        graph: 977,
      },
      expansionHeading: transformedProducts.fiber500?.copy,
    },
    fiberGig: {
      title: <span { ...setHtml( transformedProducts.fiberGig?.heading ) } />,
      speed: "1000/1000 Mbps",
      features: transformedProducts.fiberGig?.features ?? [],
      tooltip:
        "per month w/ bank Auto Pay & Paperless Bill. Max wired speed 1000/1000 Mbps. Wi-Fi, actual & average speeds vary. One-time charges apply.",
      class: "_1g",
      cardBanner: transformedProducts.fiberGig?.banner ?? {},
      speedVisuals: {
        handle: "20%",
        graph: 888,
      },
      expansionHeading: transformedProducts.fiberGig?.copy,
    },
    fiber2Gig: {
      title: <span { ...setHtml( transformedProducts.fiber2Gig?.heading ) } />,
      speed: "2000/2000 Mbps",
      features: transformedProducts.fiber2Gig?.features ?? [],
      tooltip:
        "per month w/ bank Auto Pay & Paperless Bill. Max wired speed 2000/2000 Mbps. Wi-Fi, actual & average speeds vary. One-time charges apply.",
      class: "_2g",
      cardBanner: transformedProducts.fiber2Gig?.banner ?? {},
      mostPopular: !isDoubleDouble,
      speedVisuals: {
        handle: "48%",
        graph: 577,
      },
      expansionHeading: transformedProducts.fiber2Gig?.copy,
    },
    fiber5Gig: {
      title: <span { ...setHtml( transformedProducts.fiber5Gig?.heading ) } />,
      speed: "5000/5000 Mbps",
      features: transformedProducts.fiber5Gig?.features ?? [],
      tooltip:
        "per month w/ bank Auto Pay & Paperless Bill. Max wired speed 5000/5000 Mbps. Wi-Fi, actual & average speeds vary. One-time charges apply.",
      class: "_5g",
      cardBanner: transformedProducts.fiber5Gig?.banner ?? {},
      speedVisuals: {
        handle: "70%",
        graph: 333,
      },
      expansionHeading: transformedProducts.fiber5Gig?.copy,
    },
    fiber7Gig: {
      title: <span { ...setHtml( transformedProducts.fiber7Gig?.heading ) } />,
      speed: "7000/7000 Mbps",
      features: transformedProducts.fiber7Gig?.features ?? [],
      tooltip:
        "per month w/ bank Auto Pay & Paperless Bill. Max wired speed 7000/7000 Mbps. Wi-Fi, actual & average speeds vary. One-time charges apply.",
      class: "_7g",
      cardBanner: transformedProducts.fiber7Gig?.banner ?? {},
      speedVisuals: {
        handle: "97%",
        graph: 0,
      },
      expansionHeading: transformedProducts.fiber7Gig?.copy,
    },
  };

  const fiberProducts = {
    fiber500: PRODUCTS["fiber-500m"],
    fiberGig: PRODUCTS["fiber-gig"],
    fiber2Gig: PRODUCTS["fiber-2-gig"],
    fiber5Gig: PRODUCTS["fiber-5-gig"],
    fiber7Gig: PRODUCTS["fiber-7-gig"],
  };

  return (
    <div className={styles.component__sliderAndControls}>
      <div
        className={`l-container ${styles.component__mainContainer} ${
          customStyles?.component__mainContainer || ""
        } ${productsLength > 5 && styles.component__mainContainer_gradient}`}
      >
        <div
          ref={sliderRef}
          className={`keen-slider ${styles.component__slider} ${
            customStyles?.component__slider || ""
          }`}
        >
          {products.map((product: keyof FiberPlans, index) => {
            const {
              title,
              class: $class,
              mostPopular,
              speed,
              tooltip,
              cardBanner,
            } = supplementalData[product];

            return (
              <div
                key={index}
                className={`${styles.component__slide} keen-slider__slide`}
              >
                <div
                  className={`${styles?.component__speedVisuals} ${
                    styles[`component__speedVisuals${$class}`]
                  }`}
                >
                  <div
                    className={`u-theme-light ${styles.component__card} ${
                      mostPopular ? styles.component__cardBestValue : ""
                    } ${customStyles && customStyles?.component__card}`}
                  >
                    {(mostPopular || Object.keys(cardBanner).length > 0) && (
                      <div
                        className={`${styles.component__cardBanner} ${
                          !mostPopular &&
                          cardBanner?.theme &&
                          styles[
                            `component__cardBanner-${cardBanner.theme}`
                          ]
                        }`}
                      >
                        <span className={styles.component__cardBannerText}>
                          {cardBanner?.copy || "Most popular"}
                        </span>
                      </div>
                    )}
                    <div
                      className={`${styles.component__cardHeader} ${
                        customStyles && customStyles?.component__cardHeader
                      }`}
                    >
                      <h3
                        className={`h4 ${styles.component__cardTitle} ${
                          customStyles && customStyles?.component__cardTitle
                        }`}
                      >
                        <span>{title}</span>
                      </h3>
                      <SpeedVisual
                        id={$class}
                        product={product}
                        supplementalData={supplementalData}
                      />
                      <span className={styles.component__cardSpeed}>
                        {speed}
                      </span>
                    </div>

                    <DynamicButton
                      className={styles.component__btn}
                      type={cardsCtaType}
                      position={`${position} - ${title}`}
                      productId={fiberProducts[product].productId}
                      theme={mostPopular ? "red" : "dark"}
                      title={
                        cardsCtaType === "cart"
                          ? "Select Plan"
                          : "Call to Order"
                      }
                    />

                    <div className={styles.component__cardPriceWrapper}>
                      <span className={styles.component__cardPriceCurrency}>
                        $
                      </span>
                      <div>
                        <div className={styles.component__cardPriceMain}>
                          <span className={styles.component__cardPrice}>
                            {splitPrice(
                              stripCurrency(pricing[product].price)
                            )}
                          </span>
                          <span className={styles.component__cardPriceCents}>
                            {splitPrice(
                              stripCurrency(pricing[product].price), "cents"
                            )} [2]
                          </span>
                        </div>
                        {pricing[product].strikethroughPrice &&
                          <s className={styles.component__cardPriceBase}>
                            ${pricing[product].strikethroughPrice}
                          </s>
                        }
                      </div>
                      <div
                        className={`${styles.component__cardLegalWrapper} ${
                          customStyles?.component__cardLegalWrapper || ""
                        }`}
                      >
                        <p
                          className={`${styles.component__cardProxLegal} ${
                            customStyles?.component__cardProxLegal || ""
                          }`}
                        >
                          {fiberProducts[product]?.legal?.short}
                          <Tooltip content={tooltip} />
                        </p>
                      </div>
                    </div>

                    <div className={styles.component__featuresExpansionWrapper}>
                      <p
                        className={styles.component__featuresExpansionHeading}
                        data-match-height="product-heading"
                      >
                        {injectLegalAndTooltips(
                          supplementalData[product].expansionHeading
                        )}
                      </p>

                      <ul
                        id={`js-value-props-${product}`}
                        className={`${styles.component__featureList} ${
                          customStyles?.component__featureList || ""
                        }`}
                      >
                        {supplementalData[product].features.map(
                          (feature: string, index3: number) => (
                            <li
                              className={styles.component__feature}
                              key={index3}
                            >
                              <span
                                className={`icon icon-checkmark-small ${styles.component__featureIcon}`}
                              />
                              <p
                                className={styles.component__featureText}
                                {...setHtml(feature)}
                              />
                            </li>
                          )
                        )}
                      </ul>
                      <div>
                        <button
                          id={`js-expansion-btn-${product}`}
                          className={`${styles.component__expansionButton} ${
                            mostPopular
                              ? styles?.component__expansionButtonBestValue
                              : ""
                          }`}
                          onClick={() => toggleDetails(product)}
                        >
                          More details{" "}
                          <span
                            className={`icon icon-chevron-down ${styles.component__iconChevron}`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {sliderLoaded && instanceRef.current && (
        <div
          className={`${styles.component__navigationContainer} ${
            customStyles?.component__navigationContainer || ""
          }`}
        >
          {/* Previous Arrow */}
          <button
            className={`icon icon-chevron-left1 ${
              styles.component__sliderBtnPrev
            } ${customStyles?.component__sliderBtnPrev || ""}`}
            onClick={() => {
              instanceRef.current?.prev();
            }}
          />

          {/* Pagination Bullets */}
          <div className={styles.component__dots}>
            {[
              ...Array.from(
                Array(instanceRef.current.track.details.slides.length).keys()
              ),
            ].map((idx) => {
              return (
                <div
                  key={idx}
                  onClick={() => {
                    instanceRef.current?.moveToIdx(idx);
                  }}
                  className={
                    styles.component__dot +
                    (currentSlide === idx
                      ? ` ${styles.component__dotActive}`
                      : "")
                  }
                ></div>
              );
            })}
          </div>

          {/* Next Arrow */}
          <button
            className={`icon icon-chevron-right1 ${
              styles.component__sliderBtnNext
            } ${customStyles?.component__sliderBtnNext || ""}`}
            onClick={() => {
              instanceRef.current?.next();
            }}
          />
        </div>
      )}
    </div>
  );
};

Cards.displayName = "Cards";

export default Cards;
